import { BASE_URL } from "@/Store/Action";
import { ModelCampaignWinnerGuidelinesInfoDTO } from "@models/ModelCampaignWinnerGuidelinesInfoDTO";
import { WgInfoGet200Response } from "@models/WgInfoGet200Response";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "flowbite-react";
interface GuidelineItem {
  title: string;
  description: string;
}

const warningItems = [
  "마감일까지 리뷰등록을 하지 않으면, 패널티가 부가됩니다. 패널티를 받지 않으시려면 꼭 광고주(위유체험단)와 협의 후 체험 연장을 해 주셔야 됩니다. 이부분을 지켜주지 않을시, 위유체험단의 모든 체험에서 제외됩니다.",
  "체험 후 리뷰 장기간 미등록 및 연락이 두절 시, 해당 업체측과 협조하여 강경(법적) 조치를 취할 수 있습니다.",
  "리뷰 기간 중 부득이하게 블로그를 이전하게 되더라도 꼭 사전에신청해주신 아이디의 블로그에 포스팅을 완료해 주셔야 합니다.",
  "작성해 주신 리뷰는 6 개월 이상, 필수로 유지 부탁드립니다. (해당리뷰는 마케팅 또는 업체 홍보용으로 재사용 될 수 있습니다)공감, 댓글, 스크랩은 꼭!! 허용으로 해 주세요 (막아 두시면 선정이되지 않습니다)",
  "인스타그램 릴스로 올려주시고 공유해주시면, 추후 다른 체험 선정시 우선권을 드립니다.",
  "매장 방문과 이용, 예약 관련 업체측과 분쟁에 대해 위유체험단은개입 또는 책임지지 않습니다.",
  "포스팅에 올리신 이미지는 위유체험단의 마케팅 용도로 재사용 될 수있습니다. (보통 재사용시 원본 이미지를 요청 드리는 편입니다.)",
  "기본적으로 포장 캠페인이 아닌 경우 매장이용이 필수이며, 포장은불가능합니다. (초과비용 본인 부담/타 쿠폰 중복적용 불가/테이크 아웃 불가)",
];

function Guideline({
  onNext,
  guideline,
  isPreview = false,
}: {
  onNext: (data: any) => void;
  guideline: any;
  isPreview?: boolean;
}) {
  const { secretkey } = useParams<{ secretkey: string }>();

  const { data: apiData, isLoading } =
    useQuery<ModelCampaignWinnerGuidelinesInfoDTO | null>({
      queryKey: ["campaign-wg", secretkey],
      queryFn: async () => {
        const res = await fetch(`${BASE_URL}/wg/info?secret_key=${secretkey}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          throw new Error("Failed to fetch guideline info");
        }

        const data: WgInfoGet200Response = await res.json();
        return data?.data || null;
      },
      enabled: !!secretkey && !isPreview,
    });

  const displayData = isPreview ? guideline : apiData;
  const isPackageType = guideline?.campaign_type === "배송형";

  if (!isPreview && isLoading)
    return (
      <div>
        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
          <span className="text-sm text-gray-600">
            예약 가능한 시간을 불러오는 중...
          </span>
        </div>
      </div>
    );
  if (!displayData) return <div>데이터를 불러올 수 없습니다.</div>;

  function removeBrackets(text: string) {
    return text.replace(/[\[\(].*?[\]\)]/g, "").trim();
  }
  const guidelines = displayData.posting_keyword_guidelines
    ? JSON.parse(displayData.posting_keyword_guidelines)
    : [];

  return (
    <>
      {/* Title */}
      <div className="border pb-2 mb-5 flex flex-col gap-1 ps-3 bg-gray-100 rounded-lg pt-5">
        <h2 className="text-sm font-bold text-gray-800 mb-1">
          {displayData.title}
        </h2>
        <p className="text-xs text-gray-400 mb-4">
          체험단 진행은 업무님과 블로거님 간의 소중한 약속입니다.
        </p>
      </div>

      <div className="bg-[#F4F5FF] rounded-lg p-4 mb-4 text-xs">
        <div className="flex flex-col gap-2">
          {/* Row 1 */}
          <div className="flex">
            <div className="w-1/4 flex-shrink-0 text-gray-400">체험기간</div>
            <div className="text-gray-800">
              {displayData.review_start_date} ~ {displayData.review_end_date}
            </div>
          </div>

          {/* Row 2 */}
          <div className="flex">
            <div className="w-1/4 flex-shrink-0 text-gray-400">작성기간</div>
            <div className="text-gray-800">{displayData.review_period}</div>
          </div>

          {/* Row 3 */}
          <div className="flex">
            <div className="w-1/4 flex-shrink-0 text-gray-400">캠페인</div>
            <div className="text-gray-800">
              {removeBrackets(String(displayData.title))}
            </div>
          </div>

          {/* Row 4 */}
          <div className="flex">
            <div className="w-1/4 flex-shrink-0 text-gray-400">주소</div>
            <div className="text-gray-800">{displayData.address}</div>
          </div>

          {/* Row 5 */}
          {!isPackageType && (
            <div className="flex">
              <div className="w-1/4 flex-shrink-0 text-gray-400">방문시간</div>
              <div className="text-gray-800 whitespace-pre-line">
                {displayData.visit_time}
              </div>
            </div>
          )}

          {/* Row 6 */}
          <div className="flex">
            <div className="w-1/4 flex-shrink-0 text-gray-400">제공내역</div>
            <div className="text-gray-800">
              {displayData.provision_details
                .replace(/<[^>]*>?/g, "")
                .replace("&nbsp;", " ")}
            </div>
          </div>

          {/* Row 7 */}
          {!isPackageType && (
            <div className="flex">
              <div className="w-1/4 flex-shrink-0 text-gray-400">예약번호</div>
              <div className="text-gray-800">{displayData.reservation_no}</div>
            </div>
          )}

          {/* Row 8 */}
          <div className="flex">
            <div className="w-1/4 flex-shrink-0 text-gray-400">체험방법</div>
            <div className="text-gray-800 whitespace-pre-line">
              {displayData.experience_method}
            </div>
          </div>

          {/* Row 9 */}
          <div className="flex">
            <div className="w-1/4 flex-shrink-0 text-gray-400">전화번호</div>
            <div className="text-gray-800">
              <a
                href={`tel:${displayData.phone}`}
                className="text-blue-600 underline"
              >
                {displayData.phone}
              </a>
            </div>
          </div>
        </div>

        <p className="pt-4 text-red-500">
          ★질문이나 특별 요청 사항이 있으시면 마케터에게 문의하세요.
        </p>
      </div>

      {/* 주의사항 */}
      <div className="bg-white px-4 py-7 border-y">
        <h3 className="text-red-500 font-bold mb-3 text-sm">☆ 주의사항 ☆</h3>
        <ul className="text-xs list-disc list-inside space-y-2">
          <li className="font-bold">체험기간을 꼭 지켜주세요.</li>
          <li>방문사항이 발생할 경우 미리 연락주세요!</li>
          <li className="font-bold">리뷰 작성이 기간내 이루어지지 않거나,</li>
          <li>
            노쇼가 발생할 시 비용이 청구될 수 있으니 꼭 진행 부탁드립니다.
          </li>
          <li>기한내 체험이 어려우면 꼭 연락해주세요.</li>
          <li className="font-bold">
            예약을 한 경우, 시간을 꼭 엄수해 주세요.
          </li>
          <li>늦으면 업주(예약 담당자)에게 미리 연락해주세요.</li>
        </ul>
      </div>

      {/* 공통사항 */}
      <div className="bg-white px-4 py-7 border-b">
        <h3 className="font-bold mb-3 text-sm">공통사항</h3>
        <ol className="text-xs list-decimal list-inside space-y-2">
          {displayData.common_things ? (
            displayData.common_things
              .split("\n")
              .map(
                (item: string, idx: number) =>
                  item.trim() && (
                    <li key={idx}>{item.replace(/^- /, "").trim()}</li>
                  )
              )
          ) : (
            <li className="text-xs">데이터가 없습니다.</li>
          )}
        </ol>
      </div>

      {/* 블로그 작성 가이드  */}
      <div className="px-4 py-7 ">
        <h3 className="font-bold mb-3 text-sm">블로그 작성 가이드</h3>
        <ul className="space-y-4">
          {guidelines.length > 0 ? (
            guidelines.map(
              (item: GuidelineItem, i: number) =>
                item.title && (
                  <li key={i} className="flex flex-col gap-1">
                    <div className="flex gap-2 items-start">
                      <div className="w-4 h-4 rounded-full bg-[#46B6D0] flex items-center justify-center mt-1 flex-shrink-0">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span className="text-[#46B6D0] text-sm font-medium">
                        {item.title}
                      </span>
                    </div>
                    {item.description && (
                      <div className="pl-5 text-gray-500 text-xs mt-1 whitespace-pre-line">
                        {item.description}
                      </div>
                    )}
                  </li>
                )
            )
          ) : (
            <li className="text-xs text-gray-500">데이터가 없습니다.</li>
          )}
        </ul>
      </div>

      {/* 주 의 사 항 (필독) */}
      <div className="bg-white px-4 pt-7">
        <h3 className="font-bold mb-3 text-sm">주의사항 (필독)</h3>
        {warningItems.map((text, index) => (
          <div key={index} className="flex gap-1 text-gray-500 mb-2">
            <div className="text-xs pt-[1.5px]">{index + 1}.</div>
            <div className="text-xs leading-5">{text}</div>
          </div>
        ))}
      </div>

      {/* Fixed Button - Hide in preview mode */}
      {!isPreview && (
        <div className="fixed z-20 bottom-0 left-0 right-0 bg-white border-t px-4 py-3 max-w-xl mx-auto">
          <div className="flex justify-end items-center">
            <div
              onClick={() => onNext(displayData)}
              className="flex ps-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center"
            >
              <div className="font-semibold"> 다음</div>
              <ChevronRight className=""></ChevronRight>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Guideline;
